import React, { useState } from 'react';
import { Bell, Calendar, Plus, Trash2, CheckCircle, Clock, Sparkles, Car, Zap, ShieldCheck, Info, Search, Loader2, X } from 'lucide-react';
import { Reminder, ReminderType, Language } from '../types';
import { suggestReminders } from '../services/geminiService';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';

interface CustomerRemindingProps {
  reminders: Reminder[];
  onAdd: (reminders: Reminder[]) => void;
  onDelete: (id: string) => void;
  onToggleComplete: (id: string) => void;
  onSearchShop: (keyword: string) => void;
}

export const CustomerReminding: React.FC<CustomerRemindingProps> = ({ 
  reminders, onAdd, onDelete, onToggleComplete, onSearchShop 
}) => {
  const { language } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [smartInput, setSmartInput] = useState('');
  const [isSuggesting, setIsSuggesting] = useState(false);
  
  const [manualTitle, setManualTitle] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualType, setManualType] = useState<ReminderType>('MAINTENANCE');
  const [manualDate, setManualDate] = useState('');

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Maintenance Reminders', KH: 'ការរំលឹកការថែទាំ', ZH: '维护提醒', JA: 'メンテナンス通知', KO: '유지보수 알림' },
    sub: { EN: 'Track oil changes, tax payments, and product warranties with ease.', KH: 'តាមដានការប្តូរប្រេងម៉ាស៊ីន ការបង់ពន្ធ និងការធានាផលិតផលដោយងាយស្រួល។', ZH: '轻松跟踪换油、缴税和产品保修。', JA: 'オイル交換、納税、製品保証を簡単に追跡。', KO: '오일 교환, 세금 납부 및 제품 보증을 쉽게 추적하십시오.' },
    add_btn: { EN: 'Add Reminder', KH: 'បន្ថែមការរំលឹក', ZH: '添加提醒', JA: 'リマインダー追加', KO: '알림 추가' },
    empty: { EN: 'Everything is under control', KH: 'អ្វីៗស្ថិតនៅក្រោមការគ្រប់គ្រង', ZH: '一切都在掌控之中', JA: 'すべて順調です', KO: '모든 것이 관리되고 있습니다' },
    empty_sub: { EN: 'No upcoming maintenance or guarantee expiries detected.', KH: 'មិនមានការថែទាំ ឬការផុតកំណត់ការធានាដែលនឹងមកដល់នោះទេ។', ZH: '未检测到即将到来的维护或保修到期。', JA: '今後のメンテナンスや保証期限は見つかりませんでした。', KO: '예정된 유지보수나 보증 만료가 없습니다.' },
    days_left: { EN: 'days left', KH: 'ថ្ងៃទៀត', ZH: '天剩余', JA: '日残り', KO: '일 남음' },
    find_shop: { EN: 'Find Shop', KH: 'ស្វែងរកហាង', ZH: '寻找店铺', JA: 'ショップを探す', KO: '상점 찾기' },
    new_title: { EN: 'New Reminder', KH: 'ការរំលឹកថ្មី', ZH: '新提醒', JA: '新規リマインダー', KO: '새 알림' },
    new_sub: { EN: 'Stay on top of maintenance', KH: 'តាមដានការថែទាំឱ្យបានខ្ជាប់ខ្ជួន', ZH: '掌控维护进度', JA: 'メンテナンスを常に把握', KO: '유지보수 일정을 관리하세요' },
    smart_add: { EN: 'AI Smart Add', KH: 'បន្ថែមដោយ AI ឆ្លាតវៃ', ZH: 'AI 智能添加', JA: 'AI スマート追加', KO: 'AI 스마트 추가' },
    smart_placeholder: { EN: 'e.g. I bought a Hilux today with 3 years warranty...', KH: 'ឧទាហរណ៍៖ ខ្ញុំបានទិញឡាន Hilux ថ្ងៃនេះជាមួយការធានា ៣ ឆ្នាំ...', ZH: '例如：我今天买了一台海拉克斯，保修 3 年...', JA: '例：今日、3年保証のハイラックスを購入しました...', KO: '예: 오늘 3년 보증이 포함된 하이럭스를 구매했습니다...' },
    generate_btn: { EN: 'Generate Smart Reminders', KH: 'បង្កើតការរំលឹកដោយស្វ័យប្រវត្តិ', ZH: '生成智能提醒', JA: 'スマートリマインダーを生成', KO: '스마트 알림 생성' },
    manual_label: { EN: 'Or manual Entry', KH: 'ឬ បញ្ចូលដោយខ្លួនឯង', ZH: '或手动输入', JA: '或者手動入力', KO: '또는 수동 입력' },
    event_title: { EN: 'Event Title', KH: 'ចំណងជើងព្រឹត្តិការណ៍', ZH: '事件标题', JA: 'イベント名', KO: '이벤트 제목' },
    category: { EN: 'Category', KH: 'ប្រភេទ', ZH: '类别', JA: 'カテゴリ', KO: '카테고리' },
    due_date: { EN: 'Due Date', KH: 'កាលបរិច្ឆេទកំណត់', ZH: '到期日期', JA: '期日', KO: '만료일' },
    save: { EN: 'Save Reminder', KH: 'រក្សាទុកការរំលឹក', ZH: '保存提醒', JA: 'リマインダーを保存', KO: '알림 저장' },
    maintenance: { EN: 'Maintenance', KH: 'ការថែទាំ', ZH: '维护', JA: 'メンテナンス', KO: '유지보수' },
    oil_change: { EN: 'Oil Change', KH: 'ប្តូរប្រេងម៉ាស៊ីន', ZH: '换油', JA: 'オイル交換', KO: '오일 교환' },
    guarantee: { EN: 'Guarantee', KH: 'ការធានា', ZH: '保修', JA: '保証', KO: '보증' },
    tax: { EN: 'Tax/Legal', KH: 'ពន្ធដារ/ច្បាប់', ZH: '税务/法律', JA: '税金/法務', KO: '세금/법무' },
    other: { EN: 'Other', KH: 'ផ្សេងៗ', ZH: '其他', JA: 'その他', KO: '기ត' },
    confirm_del: { EN: 'Delete this reminder?', KH: 'តើអ្នកចង់លុបការរំលឹកនេះមែនទេ?', ZH: '删除此提醒？', JA: 'このリマインダーを削除しますか？', KO: '이 알림을 삭제하시겠습니까?' }
  };

  const handleSmartAdd = async () => {
    if (!smartInput.trim()) return;
    setIsSuggesting(true);
    const suggestions = await suggestReminders(smartInput, language);
    if (suggestions.length > 0) {
      const newReminders: Reminder[] = suggestions.map((s: any) => ({
        id: `rem-${Date.now()}-${Math.random()}`,
        customerId: 'u1',
        title: s.title,
        description: s.description,
        type: s.type as ReminderType,
        dueDate: s.dueDate,
        remindAt: s.dueDate,
        isCompleted: false,
        category: s.category
      }));
      onAdd(newReminders);
      setSmartInput('');
      setIsAdding(false);
    }
    setIsSuggesting(false);
  };

  const handleManualAdd = () => {
    if (!manualTitle || !manualDate) return;
    onAdd([{
      id: `rem-${Date.now()}`,
      customerId: 'u1',
      title: manualTitle,
      description: manualDesc,
      type: manualType,
      dueDate: manualDate,
      remindAt: manualDate,
      isCompleted: false,
      category: 'General'
    }]);
    setManualTitle('');
    setManualDesc('');
    setManualDate('');
    setIsAdding(false);
  };

  const confirmRemove = (id: string) => {
    if (window.confirm(texts.confirm_del[language])) {
      onDelete(id);
    }
  };

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-5xl mx-auto animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
        <div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter mb-4">{texts.title[language].split(' ')[0]} <span className="text-violet-600">{texts.title[language].split(' ').slice(1).join(' ')}</span></h1>
          <p className="text-slate-500 font-bold text-lg max-w-xl">{texts.sub[language]}</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="bg-slate-900 text-white px-8 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-xl hover:bg-violet-600 transition-all flex items-center gap-3 shrink-0"><Plus size={20} strokeWidth={3} /> {texts.add_btn[language]}</button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {reminders.length === 0 ? (
          <div className="py-32 bg-white rounded-[3rem] border-2 border-dashed border-slate-100 flex flex-col items-center justify-center opacity-30 text-center">
            <Bell size={64} className="mb-6 text-slate-200" /><p className="text-2xl font-black tracking-tight text-slate-400">{texts.empty[language]}</p><p className="text-sm font-bold text-slate-300 uppercase mt-2 px-8">{texts.empty_sub[language]}</p>
          </div>
        ) : (
          reminders.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()).map(rem => (
            <div key={rem.id} className={`bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 group flex flex-col md:flex-row items-center gap-8 ${rem.isCompleted ? 'opacity-50' : ''}`}>
              <div className={`w-16 h-16 rounded-3xl flex items-center justify-center shrink-0 border-2 shadow-sm ${rem.type === 'OIL_CHANGE' ? 'bg-amber-50 text-amber-600' : rem.type === 'GUARANTEE' ? 'bg-emerald-50 text-emerald-600' : rem.type === 'TAX' ? 'bg-rose-50 text-rose-600' : 'bg-violet-50 text-violet-600'}`}>
                {rem.type === 'OIL_CHANGE' ? <Car size={24} /> : rem.type === 'GUARANTEE' ? <ShieldCheck size={24} /> : rem.type === 'TAX' ? <Info size={24} /> : <Bell size={24} />}
              </div>
              <div className="flex-1 text-center md:text-left min-w-0">
                <div className="flex flex-col md:flex-row md:items-center gap-2 mb-1"><h3 className="text-xl font-black text-slate-900 truncate">{rem.title}</h3><span className="inline-block px-3 py-1 rounded-lg text-[9px] font-black uppercase bg-slate-50 text-slate-400 border border-slate-100">{rem.type.replace('_', ' ')}</span></div>
                <p className="text-slate-400 font-bold text-sm line-clamp-2">{rem.description}</p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-[10px] font-black uppercase tracking-widest text-slate-400 mt-4">
                  <div className="flex items-center gap-2"><Calendar size={14} className="text-violet-500" /> {new Date(rem.dueDate).toLocaleDateString()}</div>
                  <div className="flex items-center gap-2"><Clock size={14} className="text-violet-500" /> {Math.ceil((new Date(rem.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} {texts.days_left[language]}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {!rem.isCompleted && <button onClick={() => onSearchShop(rem.title)} className="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-violet-50 hover:text-violet-600 transition-all flex items-center gap-2 font-black uppercase text-[9px]"><Search size={16} /> {texts.find_shop[language]}</button>}
                <button onClick={() => onToggleComplete(rem.id)} className={`p-4 rounded-2xl border ${rem.isCompleted ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-300'}`}><CheckCircle size={20} /></button>
                <button onClick={() => confirmRemove(rem.id)} className="p-4 bg-red-50 text-red-400 rounded-2xl hover:bg-red-100 transition-all"><Trash2 size={20} /></button>
              </div>
            </div>
          ))
        )}
      </div>

      {isAdding && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-[3rem] w-full max-w-xl shadow-2xl animate-in zoom-in duration-300 relative overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white shrink-0">
              <div className="flex items-center gap-4"><div className="w-12 h-12 bg-violet-600 rounded-2xl flex items-center justify-center text-white shadow-xl"><Bell size={24} fill="currentColor" /></div><div><h2 className="text-xl font-black text-slate-900 tracking-tight">{texts.new_title[language]}</h2><p className="text-[10px] text-slate-400 font-black uppercase">{texts.new_sub[language]}</p></div></div>
              <button onClick={() => setIsAdding(false)} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 transition-all"><X size={20} /></button>
            </div>
            <div className="p-10 space-y-10 overflow-y-auto no-scrollbar">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-violet-600 uppercase flex items-center gap-2">
                  <Sparkles size={14} fill="currentColor" /> {texts.smart_add[language]} <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <textarea value={smartInput} onChange={(e) => setSmartInput(e.target.value)} placeholder={texts.smart_placeholder[language]} className="w-full p-6 rounded-[2rem] bg-slate-50 font-bold text-slate-900 outline-none h-28 resize-none shadow-inner" />
                <button onClick={handleSmartAdd} disabled={isSuggesting || !smartInput.trim()} className="w-full py-5 bg-violet-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-violet-700 transition-all flex justify-center items-center gap-3 disabled:opacity-50">{isSuggesting ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} fill="currentColor" />} {texts.generate_btn[language]}</button>
              </div>
              <div className="relative py-4 flex items-center"><div className="flex-grow border-t border-slate-100"></div><span className="flex-shrink mx-4 text-[10px] font-black text-slate-300 uppercase">{texts.manual_label[language]}</span><div className="flex-grow border-t border-slate-100"></div></div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                   <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase">
                      {texts.event_title[language]} <span className="text-rose-500 ml-0.5">*</span>
                    </label>
                    <input type="text" value={manualTitle} onChange={e => setManualTitle(e.target.value)} className="w-full p-4 rounded-xl bg-slate-50 font-black text-slate-900 outline-none" />
                   </div>
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase">
                      {texts.category[language]} <span className="text-rose-500 ml-0.5">*</span>
                     </label>
                     <select value={manualType} onChange={e => setManualType(e.target.value as ReminderType)} className="w-full p-4 rounded-xl bg-slate-50 font-black text-slate-900 outline-none appearance-none">
                        <option value="MAINTENANCE">{texts.maintenance[language]}</option><option value="OIL_CHANGE">{texts.oil_change[language]}</option><option value="GUARANTEE">{texts.guarantee[language]}</option><option value="TAX">{texts.tax[language]}</option><option value="CUSTOM">{texts.other[language]}</option>
                     </select>
                   </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase">
                    {texts.due_date[language]} <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)} className="w-full p-4 rounded-xl bg-slate-50 font-black text-slate-900 outline-none" />
                </div>
                <button onClick={handleManualAdd} disabled={!manualTitle || !manualDate} className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase hover:bg-slate-800 transition-all shadow-xl disabled:opacity-30">{texts.save[language]}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};